import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  DialogContent,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  TextField
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { GenericDialog } from '../../components/GenericDialog';
import SectionEmptyState from '../../components/SectionEmptyState';
import { PATH_DASHBOARD } from '../../routes/paths';
import useLocales from '../../hooks/useLocales';
import type { Pathway } from '../../pages/pathways-page/types';
import { fetchOwnedPathways } from './api';
import { getPathwaysImagePath } from '../../common/getImagePath';

interface AddTopicDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AddTopicDialog({ open, onClose }: AddTopicDialogProps) {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const [pathways, setPathways] = useState<Pathway[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setError(false);
    setIsLoading(true);
    fetchOwnedPathways()
      .then(setPathways)
      .catch(() => setError(true))
      .finally(() => setIsLoading(false));
  }, [open]);

  const filtered = search.trim()
    ? pathways.filter((p) => p.title.toLowerCase().includes(search.toLowerCase()))
    : pathways;

  const handleSelect = (pathway: Pathway) => {
    onClose();
    navigate(`${PATH_DASHBOARD.pathway.path}/${pathway.slug}`);
  };

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title={translate('builder.add_topic')}
      maxWidth="sm"
      fullWidth
      paperSx={{ height: 520 }}
    >
      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        <Box sx={{ px: 2.5, pt: 1.5, pb: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={translate('pathways.search_placeholder') as string}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Divider />

        {error ? (
          <Box sx={{ px: 2.5, pt: 2 }}>
            <Alert severity="error">{translate('general.error_loading')}</Alert>
          </Box>
        ) : isLoading ? (
          <Stack spacing={1} sx={{ px: 2.5, pt: 1.5 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 1 }} />
            ))}
          </Stack>
        ) : pathways.length === 0 ? (
          <Box sx={{ py: 4 }}>
            <SectionEmptyState
              icon={<AccountTreeOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />}
              title={translate('explore.my_pathways.empty_title')}
              description={translate('explore.my_pathways.empty_description')}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    onClose();
                    navigate(PATH_DASHBOARD.pathway.new);
                  }}
                >
                  {translate('explore.my_pathways.create_first_pathway')}
                </Button>
              }
            />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 4 }}>
            <SectionEmptyState
              icon={<AccountTreeOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />}
              title={(translate('pathways.no_results_with_query') as string).replace(
                '{{query}}',
                search
              )}
            />
          </Box>
        ) : (
          <List disablePadding sx={{ pt: 0.5, pb: 1 }}>
            {filtered.map((pathway) => (
              <ListItemButton
                key={pathway.guid}
                onClick={() => handleSelect(pathway)}
                sx={{ px: 2.5, py: 1, gap: 1.5, borderRadius: 0 }}
              >
                <Box
                  component="img"
                  src={
                    pathway.imageUrl
                      ? getPathwaysImagePath(pathway.imageUrl)
                      : '/static/pathway/default.svg'
                  }
                  alt={pathway.title}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    objectFit: 'cover',
                    flexShrink: 0,
                    bgcolor: 'background.neutral'
                  }}
                />
                <ListItemText
                  primary={pathway.title}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </GenericDialog>
  );
}
